const FEATURE = {
  src: "/assets/rammed-earth-stairwell.webp",
  alt: "Rammed earth interior stairwell showing the stratified sediment layers of compacted natural soil walls",
  title: "Rammed Earth, Up Close",
  note: "Every horizontal striation is a compacted lift of natural soil — the wall is quite literally made of the land it stands on. Photographed as reference for the Institute's natural build curriculum.",
  testid: "building-feature-image",
};

const TECHNIQUES = [
  {
    src: "/assets/rammed-earth-stairwell.webp",
    alt: "Stratified rammed earth wall texture in warm sediment tones",
    name: "Rammed Earth",
    tagline: "Strength, beauty, and durability",
    testid: "technique-rammed-earth",
    what: "Moist natural soil is poured into formwork and compacted in layers, forming monolithic load-bearing walls with striking sediment striations.",
    benefits:
      "Incredibly durable, fire resistant, low maintenance, and exceptional thermal mass — walls that buffer desert heat by day and release warmth at night. With proper formwork preparation and labor organization, walls rise faster than traditional blockwork.",
    best: "Modern homes, public buildings, retaining walls, and feature walls.",
  },
  {
    src: "/assets/cob-cottage.jpg",
    alt: "Hand-sculpted cob cottage with organic curved earthen walls and thatched roof at golden hour",
    name: "Cob",
    tagline: "Shaped by hand, rooted in nature",
    testid: "technique-cob",
    what: "A mix of clay, sand, straw, and water, sculpted by hand without forms into organic shapes and strong, insulated structures.",
    benefits:
      "Excellent insulation, fire resistant, repairable, and promotes healthy indoor environments — walls that breathe with the seasons.",
    best: "Homes, natural buildings, garden structures, and artistic spaces.",
  },
  {
    src: "/assets/quincha-wall.jpg",
    alt: "Quincha wall under construction — bamboo frame infilled with woven cane and coated in earthen plaster",
    name: "Quincha",
    tagline: "Tradition, flexibility, sustainable by nature",
    testid: "technique-quincha",
    what: "A lightweight technique using a wooden or bamboo frame infilled with cane, reeds, or sticks, then coated with earth.",
    benefits:
      "Lightweight, flexible, breathable, and famously earthquake-resistant — built from local, renewable materials.",
    best: "Rural housing, cultural heritage projects, eco-resorts, and community buildings.",
  },
];

const NaturalBuilding = () => {
  return (
    <section
      id="natural-building"
      data-testid="natural-building-section"
      className="py-24 px-6 border-b border-stone-800 bg-stone-950/40"
    >
      <div className="max-w-7xl mx-auto space-y-16">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5 space-y-4">
            <span className="mono-tag text-xs uppercase tracking-widest text-amber-500 block">
              Edification &amp; Self-Sufficiency
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-stone-100 display-serif">
              Natural Building &amp; Earthen Architecture
            </h2>
            <p className="text-stone-400 text-sm leading-relaxed">
              The same hands that tend the fruit forests learn to raise shelter from the land
              itself. The Institute teaches earthen construction — rammed earth, cob, and
              quincha — through hands-on natural build workshops, with structures planned for
              the Institute's own acreage.
            </p>
            <div className="grid grid-cols-2 gap-4 pt-2">
              <div className="p-4 rounded-xl bg-stone-950 border border-stone-800">
                <span className="block text-2xl font-extrabold text-amber-400 display-serif">
                  3
                </span>
                <span className="text-xs text-stone-400">Earthen techniques in the curriculum</span>
              </div>
              <div className="p-4 rounded-xl bg-stone-950 border border-stone-800">
                <span className="block text-2xl font-extrabold text-amber-400 display-serif">
                  0%
                </span>
                <span className="text-xs text-stone-400">
                  Cement required — local soil, minimal waste, low carbon
                </span>
              </div>
            </div>
            <div className="space-y-3 pt-2">
              <div className="flex items-start gap-3 text-xs text-stone-400">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-500 mt-1.5 shrink-0"></span>
                <span>
                  <strong className="text-stone-300">Workshops, not lectures:</strong> members
                  learn formwork, soil mixing, and plastering by building real walls.
                </span>
              </div>
              <div className="flex items-start gap-3 text-xs text-stone-400">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-500 mt-1.5 shrink-0"></span>
                <span>
                  <strong className="text-stone-300">Structures on the land:</strong> workshop
                  builds rise on Institute acreage — shelter and gathering spaces made by the
                  membership, for the membership.
                </span>
              </div>
              <div className="flex items-start gap-3 text-xs text-stone-400">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-500 mt-1.5 shrink-0"></span>
                <span>
                  <strong className="text-stone-300">Desert-tuned:</strong> earthen thermal mass
                  is the Mojave's oldest climate control — cool interiors without the grid.
                </span>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <figure
              data-testid={FEATURE.testid}
              className="rounded-xl border border-stone-800 bg-stone-950 p-4 space-y-3"
            >
              <div className="rounded-lg border border-stone-800 overflow-hidden max-h-[560px]">
                <img
                  src={FEATURE.src}
                  alt={FEATURE.alt}
                  className="w-full h-full max-h-[560px] object-cover object-center block"
                  loading="lazy"
                />
              </div>
              <figcaption className="space-y-1">
                <div className="text-sm font-bold text-stone-100">{FEATURE.title}</div>
                <div className="text-[11px] text-stone-400 leading-relaxed">{FEATURE.note}</div>
              </figcaption>
            </figure>
          </div>
        </div>

        <div className="space-y-8">
          <div className="space-y-2">
            <span className="mono-tag text-xs uppercase tracking-widest text-amber-500 block">
              The Curriculum
            </span>
            <h3 className="text-xl sm:text-2xl font-bold text-stone-100 display-serif">
              Three Ways to Build with the Earth
            </h3>
            <p className="text-xs text-stone-400 max-w-2xl leading-relaxed">
              Each technique is taught end-to-end: what it is, why it performs, and where it
              belongs. All three use abundant local materials and centuries-proven craft.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {TECHNIQUES.map((tech) => (
              <figure
                key={tech.testid}
                data-testid={tech.testid}
                className="rounded-xl border border-stone-800 bg-stone-950 p-4 space-y-3 flex flex-col"
              >
                <div className="aspect-[4/3] rounded-lg border border-stone-800 overflow-hidden">
                  <img
                    src={tech.src}
                    alt={tech.alt}
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                    loading="lazy"
                  />
                </div>
                <figcaption className="space-y-2 flex-1">
                  <div>
                    <div className="text-sm font-bold text-stone-100">{tech.name}</div>
                    <div className="text-[10px] uppercase tracking-widest text-amber-500/90">
                      {tech.tagline}
                    </div>
                  </div>
                  <p className="text-[11px] text-stone-400 leading-relaxed">{tech.what}</p>
                  <p className="text-[11px] text-stone-400 leading-relaxed border-t border-stone-800/70 pt-2">
                    <span className="text-amber-400/90 font-semibold">Benefits: </span>
                    {tech.benefits}
                  </p>
                  <p className="text-[11px] text-stone-400 leading-relaxed">
                    <span className="text-amber-400/90 font-semibold">Best for: </span>
                    {tech.best}
                  </p>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default NaturalBuilding;
